import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Save,
  X,
  Plus,
  UserPlus,
  Trash2,
  Calendar,
  FileText,
  Users,
  ShieldAlert,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/auth";
import {
  createKasus,
  createKasusAPI,
  JENIS_TL,
} from "../utils/auditStorage";
import { loadHariLibur } from "../utils/hariLiburStorage";
import { hitungDeadline, formatTanggalID } from "../utils/hariKerja";
import { Toaster, toast } from "../components/ui/sonner";

const input = "block w-full rounded-md border border-gray-300 bg-white px-2.5 py-2 text-[13px] focus:border-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20";

export default function AuditInvestigasiBaru() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hariLibur] = useState(() => loadHariLibur());
  const [users, setUsers] = useState([]);

  const [form, setForm] = useState({
    nomorLHP: "",
    tanggalLHP: "",
    judulKasus: "",
    deskripsi: "",
    timPemeriksa: [],
    terperiksa: [{ namaInstansi: "", namaPejabat: "", jabatan: "", noWA: "", email: "", peranDalamKasus: "" }],
    rekomendasi: [
      {
        uraian: "",
        jenisTL: JENIS_TL.DOKUMEN,
        nominalTemuan: 0,
        nominalKembali: 0,
        dokumenDiperlukan: [],
      },
    ],
  });

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);



  const canSave =
    form.nomorLHP.trim() && form.tanggalLHP && form.judulKasus.trim() && form.terperiksa.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    const kasus = createKasus({
      ...form,
      createdBy: user?.id,
    });
    try {
      const saved = await createKasusAPI(kasus);
      toast.success("Kasus audit investigasi berhasil disimpan");
      navigate(`/audit-investigasi/${saved.id || kasus.id}`);
    } catch (err) {
      toast.error("Gagal menyimpan kasus: " + (err?.response?.data?.detail || err.message));
    }
  };

  const addTerperiksa = () =>
    setForm((f) => ({
      ...f,
      terperiksa: [...f.terperiksa, { namaInstansi: "", namaPejabat: "", jabatan: "", noWA: "", email: "", peranDalamKasus: "" }],
    }));

  const removeTerperiksa = (i) =>
    setForm((f) => ({ ...f, terperiksa: f.terperiksa.filter((_, idx) => idx !== i) }));

  const updateTerperiksa = (i, key, value) =>
    setForm((f) => ({
      ...f,
      terperiksa: f.terperiksa.map((t, idx) => (idx === i ? { ...t, [key]: value } : t)),
    }));

  const addRek = () =>
    setForm((f) => ({
      ...f,
      rekomendasi: [
        ...f.rekomendasi,
        { uraian: "", jenisTL: JENIS_TL.DOKUMEN, nominalTemuan: 0, nominalKembali: 0, dokumenDiperlukan: [] },
      ],
    }));

  const updateRek = (i, key, value) =>
    setForm((f) => ({
      ...f,
      rekomendasi: f.rekomendasi.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)),
    }));

  const removeRek = (i) =>
    setForm((f) => ({ ...f, rekomendasi: f.rekomendasi.filter((_, idx) => idx !== i) }));

  const toggleTim = (uid) =>
    setForm((f) => ({
      ...f,
      timPemeriksa: f.timPemeriksa.includes(uid)
        ? f.timPemeriksa.filter((x) => x !== uid)
        : [...f.timPemeriksa, uid],
    }));

  return (
    <div className="space-y-5" data-testid="audit-new-page">
      <Toaster position="top-right" richColors />
      <div className="flex items-end justify-between border-b border-gray-200 pb-5">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Modul Kritikal
          </div>
          <h1 className="mt-1 font-display text-2xl font-bold text-slate-900">Kasus Baru</h1>
          <p className="mt-1 text-[13px] text-slate-500">
            Deadline 60 hari kerja tindak lanjut akan dihitung setelah Anda memasukkan Tanggal Penyerahan Laporan di halaman detail kasus.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" data-testid="audit-new-form">
        {/* LHP Info */}
        <section className="rounded-lg border border-gray-200 bg-white p-5">
          <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
            <FileText className="h-3.5 w-3.5" /> Data LHP
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Nomor LHP *</div>
              <input
                type="text"
                value={form.nomorLHP}
                onChange={(e) => setForm({ ...form, nomorLHP: e.target.value })}
                data-testid="field-nomorLHP"
                placeholder="mis. LHP-001/IRBANSUS/TSM/I/2026"
                required
                className={input}
              />
            </label>
            <label>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Tanggal LHP *</div>
              <input
                type="date"
                value={form.tanggalLHP}
                onChange={(e) => setForm({ ...form, tanggalLHP: e.target.value })}
                data-testid="field-tanggalLHP"
                required
                className={input}
              />
            </label>
            <label className="md:col-span-2">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Judul Kasus *</div>
              <input
                type="text"
                value={form.judulKasus}
                onChange={(e) => setForm({ ...form, judulKasus: e.target.value })}
                data-testid="field-judulKasus"
                required
                className={input}
              />
            </label>
            <label className="md:col-span-2">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Deskripsi</div>
              <textarea
                value={form.deskripsi}
                onChange={(e) => setForm({ ...form, deskripsi: e.target.value })}
                rows={3}
                data-testid="field-deskripsi"
                className={`${input} resize-none`}
              />
            </label>
          </div>

        </section>

        {/* Tim Pemeriksa */}
        <section className="rounded-lg border border-gray-200 bg-white p-5">
          <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
            <Users className="h-3.5 w-3.5" /> Tim Pemeriksa ({form.timPemeriksa.length})
          </div>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 md:grid-cols-3">
            {users
              .filter((u) => u.role !== "IRBAN")
              .map((u) => {
                const selected = form.timPemeriksa.includes(u.id);
                return (
                  <button
                    type="button"
                    key={u.id}
                    onClick={() => toggleTim(u.id)}
                    data-testid={`tim-toggle-${u.id}`}
                    className={`flex items-center justify-between rounded-md border px-2.5 py-2 text-left text-[12px] ${
                      selected ? "border-[#1e3a5f] bg-[#e8edf2] text-[#1e3a5f]" : "border-gray-200 bg-white hover:bg-gray-50"
                    }`}
                  >
                    <span className="truncate">{u.name}</span>
                    <span className="ml-2 shrink-0 text-[9.5px] font-semibold uppercase tracking-wider text-slate-500">
                      {u.role?.replace("_", " ")}
                    </span>
                  </button>
                );
              })}
          </div>
        </section>

        {/* Terperiksa */}
        <section className="rounded-lg border border-gray-200 bg-white p-5">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
              <ShieldAlert className="h-3.5 w-3.5" /> Terperiksa ({form.terperiksa.length})
            </div>
            <button type="button" onClick={addTerperiksa} className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-[11px] font-semibold hover:border-[#1e3a5f]" data-testid="add-terperiksa">
              <UserPlus className="h-3 w-3" /> Tambah
            </button>
          </div>
          <div className="space-y-3">
            {form.terperiksa.map((t, i) => (
              <div key={i} className="rounded-md border border-gray-200 p-3" data-testid={`terperiksa-${i}`}>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
                    Terperiksa #{i + 1}
                  </div>
                  {form.terperiksa.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => removeTerperiksa(i)}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 hover:text-red-700"
                      data-testid={`remove-terperiksa-${i}`}
                    >
                      <Trash2 className="h-3 w-3" /> Hapus
                    </button>
                  ) : null}
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <input placeholder="Nama Instansi" value={t.namaInstansi} onChange={(e) => updateTerperiksa(i, "namaInstansi", e.target.value)} className={input} data-testid={`terperiksa-${i}-instansi`} />
                  <input placeholder="Nama Pejabat" value={t.namaPejabat} onChange={(e) => updateTerperiksa(i, "namaPejabat", e.target.value)} className={input} data-testid={`terperiksa-${i}-pejabat`} />
                  <input placeholder="Jabatan" value={t.jabatan} onChange={(e) => updateTerperiksa(i, "jabatan", e.target.value)} className={input} />
                  <input placeholder="Peran dalam kasus" value={t.peranDalamKasus} onChange={(e) => updateTerperiksa(i, "peranDalamKasus", e.target.value)} className={input} />
                  <input placeholder="No WA (mis. 08xxxxx)" value={t.noWA} onChange={(e) => updateTerperiksa(i, "noWA", e.target.value)} className={input} data-testid={`terperiksa-${i}-wa`} />
                  <input placeholder="Email" type="email" value={t.email} onChange={(e) => updateTerperiksa(i, "email", e.target.value)} className={input} data-testid={`terperiksa-${i}-email`} />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Rekomendasi */}
        <section className="rounded-lg border border-gray-200 bg-white p-5">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
              Rekomendasi ({form.rekomendasi.length})
            </div>
            <button type="button" onClick={addRek} className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-[11px] font-semibold hover:border-[#1e3a5f]" data-testid="add-rekomendasi">
              <Plus className="h-3 w-3" /> Tambah
            </button>
          </div>
          <div className="space-y-3">
            {form.rekomendasi.map((r, i) => (
              <div key={i} className="rounded-md border border-gray-200 p-3" data-testid={`rek-${i}`}>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
                    Rekomendasi #{i + 1}
                  </div>
                  {form.rekomendasi.length > 1 ? (
                    <button type="button" onClick={() => removeRek(i)} className="text-[11px] font-semibold text-red-600">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  ) : null}
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  <textarea placeholder="Uraian rekomendasi…" value={r.uraian} onChange={(e) => updateRek(i, "uraian", e.target.value)} rows={2} className={`${input} md:col-span-2 resize-none`} />
                  <select value={r.jenisTL} onChange={(e) => updateRek(i, "jenisTL", e.target.value)} className={input} data-testid={`rek-${i}-jenis`}>
                    <option value="DOKUMEN">Dokumen</option>
                    <option value="UANG">Uang</option>
                    <option value="KEDUANYA">Keduanya</option>
                  </select>
                  {r.jenisTL !== "DOKUMEN" ? (
                    <input placeholder="Nominal Temuan (Rp)" type="number" value={r.nominalTemuan} onChange={(e) => updateRek(i, "nominalTemuan", Number(e.target.value))} className={input} data-testid={`rek-${i}-temuan`} />
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => navigate("/audit-investigasi")}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-4 py-2 text-[12px] font-semibold text-slate-700"
            data-testid="audit-cancel"
          >
            <X className="h-3.5 w-3.5" /> Batal
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#1e3a5f] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#152a46] disabled:opacity-50"
            data-testid="audit-save"
          >
            <Save className="h-3.5 w-3.5" /> Simpan Kasus
          </button>
        </div>
      </form>
    </div>
  );
}
